import { useId } from "react";

/**
 * Ghanaian cultural design motifs — reusable decorative elements.
 *
 *  - Adinkra symbols: traditional Akan visual symbols (cultural heritage). Drawn
 *    as simple line/geometry interpretations so they read at any size.
 *  - AdinkraPattern: a faint tiling watermark for section/hero backgrounds.
 *  - KenteStripe: a woven-cloth inspired colour band for dividers and accents.
 */

export type AdinkraName =
  | "adinkrahene" // authority, greatness, charisma — concentric circles
  | "nsoromma" // child of the heavens — a star
  | "sankofa" // learn from the past — heart with a curl
  | "dwennimmen" // ram's horns — humility and strength
  | "nyamedua" // altar of God — presence of the divine
  | "eban" // fence — love, safety, security
  | "akoma" // heart — patience and goodwill
  | "fawohodie" // independence and freedom
  | "neaonnim" // learning and knowledge
  | "nkyinkyim" // initiative, adaptability, dynamism
  | "binkabi" // peace and harmony
  | "gyenyame" // supremacy of God
  | "akoben" // vigilance and readiness
  | "akofena" // courage and heroism
  | "mpatapo"; // reconciliation and peace

export const GHANA_FLAG_BORDER =
  "bg-[conic-gradient(from_135deg,#006B3F,#CE1126,#FCD116,#006B3F)]";

interface GhanaFlagMarkProps {
  className?: string;
  title?: string;
  symbol?: AdinkraName;
}

/** Round Adinkra mark used anywhere the product needs a compact brand signal. */
export function GhanaFlagMark({
  className = "",
  title = "African Art Network Adinkra mark",
  symbol = "adinkrahene",
}: GhanaFlagMarkProps) {
  return (
    <svg
      viewBox="0 0 60 60"
      className={`overflow-hidden rounded-full ${className}`}
      role={title ? "img" : "presentation"}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {title && <title>{title}</title>}
      <circle cx="30" cy="30" r="30" fill="#0f1217" />
      <circle cx="30" cy="30" r="27.5" fill="none" stroke="#FCD116" strokeWidth="3" />
      <path d="M14 6 A28 28 0 0 1 46 6" fill="none" stroke="#CE1126" strokeWidth="4" strokeLinecap="round" />
      <path d="M46 54 A28 28 0 0 1 14 54" fill="none" stroke="#006B3F" strokeWidth="4" strokeLinecap="round" />
      <g
        transform="translate(6 6) scale(0.48)"
        fill="none"
        color="#FCD116"
        stroke="currentColor"
        strokeWidth="7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {symbolPaths(symbol)}
      </g>
    </svg>
  );
}

interface GhanaTopStripProps {
  className?: string;
  fixed?: boolean;
}

/** Ghana flag tricolor accent strip for the top edge of app shells. */
export function GhanaTopStrip({ className = "", fixed = false }: GhanaTopStripProps) {
  return (
    <div
      className={`${fixed ? "fixed" : "absolute"} inset-x-0 top-0 z-[60] flex h-1.5 ${className}`}
      aria-hidden="true"
    >
      <span className="h-full w-[28%] bg-[#006B3F]" />
      <span className="h-full w-[9%] bg-[#CE1126]" />
      <span className="h-full flex-1 bg-[#FCD116]" />
    </div>
  );
}

interface AdinkraSymbolProps {
  name?: AdinkraName;
  className?: string;
  /** Stroke colour. Defaults to currentColor so it inherits text colour. */
  color?: string;
  strokeWidth?: number;
  title?: string;
}

function symbolPaths(name: AdinkraName) {
  switch (name) {
    case "adinkrahene":
      return (
        <>
          <circle cx="50" cy="50" r="28" strokeWidth="8" />
          <circle cx="50" cy="50" r="14" strokeWidth="8" />
          <circle cx="50" cy="50" r="5" fill="currentColor" stroke="none" />
          <path
            d="M50 4 L50 16 M50 84 L50 96 M4 50 L16 50 M84 50 L96 50 M18 18 L27 27 M73 73 L82 82 M82 18 L73 27 M27 73 L18 82 M31 7 L35 19 M65 81 L69 93 M7 31 L19 35 M81 65 L93 69 M69 7 L65 19 M35 81 L31 93 M93 31 L81 35 M19 65 L7 69"
            strokeWidth="8"
          />
        </>
      );
    case "nsoromma":
      return (
        <>
          <path
            fill="currentColor"
            fillRule="evenodd"
            stroke="none"
            d="M50 4 L60 34 L90 17 L74 45 L97 50 L74 55 L90 83 L60 66 L50 96 L40 66 L10 83 L26 55 L3 50 L26 45 L10 17 L40 34 Z M50 36 C42.27 36 36 42.27 36 50 C36 57.73 42.27 64 50 64 C57.73 64 64 57.73 64 50 C64 42.27 57.73 36 50 36 Z"
            clipRule="evenodd"
          />
        </>
      );
    case "sankofa":
      return (
        <>
          <path
            fill="currentColor"
            stroke="none"
            d="M22 68 C28 82 50 84 68 69 C75 63 82 59 91 59 C85 51 73 49 63 55 C52 62 39 61 31 53 C40 54 52 53 63 49 C54 43 43 42 34 45 C32 36 38 27 49 23 C58 20 65 25 65 33 C65 38 61 42 56 42 C52 42 50 39 51 35 C45 38 45 45 50 49 C60 58 79 43 73 28 C68 15 49 11 34 20 C20 29 15 50 22 68 Z"
          />
          <path fill="currentColor" stroke="none" d="M39 79 L39 94 L45 94 L45 81 Z M51 79 L51 94 L57 94 L57 77 Z" />
        </>
      );
    case "dwennimmen":
      return (
        <>
          <path
            fill="currentColor"
            stroke="none"
            d="M50 9 C60 20 63 35 63 50 C63 65 60 80 50 91 C40 80 37 65 37 50 C37 35 40 20 50 9 Z"
          />
          <path
            d="M37 31 C24 20 15 27 22 41 M63 31 C76 20 85 27 78 41 M37 69 C24 80 15 73 22 59 M63 69 C76 80 85 73 78 59"
            strokeWidth="8"
          />
          <path d="M36 34 C29 45 29 56 36 67 M64 34 C71 45 71 56 64 67" strokeWidth="7" />
        </>
      );
    case "nyamedua":
      return (
        <>
          <path d="M50 12 L50 88 M12 50 L88 50" />
          <circle cx="50" cy="22" r="8" />
          <circle cx="50" cy="78" r="8" />
          <circle cx="22" cy="50" r="8" />
          <circle cx="78" cy="50" r="8" />
          <circle cx="50" cy="50" r="6" />
        </>
      );
    case "eban":
      return (
        <>
          <rect x="16" y="15" width="25" height="25" strokeWidth="8" />
          <rect x="59" y="15" width="25" height="25" strokeWidth="8" />
          <rect x="16" y="60" width="25" height="25" strokeWidth="8" />
          <rect x="59" y="60" width="25" height="25" strokeWidth="8" />
          <rect x="41" y="40" width="18" height="20" strokeWidth="8" />
          <path d="M41 27 L59 27 M41 73 L59 73 M28 40 L28 60 M72 40 L72 60" strokeWidth="8" />
        </>
      );
    case "akoma":
      return (
        <>
          <path d="M50 84 C31 68 22 56 22 41 C22 30 30 22 40 22 C45 22 49 25 50 30 C51 25 55 22 60 22 C70 22 78 30 78 41 C78 56 69 68 50 84 Z" />
          <path d="M36 45 C42 49 46 49 50 43 C54 49 58 49 64 45" />
        </>
      );
    case "fawohodie":
      return (
        <>
          <path d="M50 13 L50 87" strokeWidth="9" />
          <path d="M48 24 C33 15 20 28 20 50 C20 72 33 85 48 76" strokeWidth="10" />
          <path d="M52 24 C67 15 80 28 80 50 C80 72 67 85 52 76" strokeWidth="10" />
          <path d="M27 25 C20 15 32 10 40 20 M27 75 C20 85 32 90 40 80 M73 25 C80 15 68 10 60 20 M73 75 C80 85 68 90 60 80" strokeWidth="7" />
        </>
      );
    case "neaonnim":
      return (
        <>
          <path d="M50 10 L50 88" strokeWidth="6" />
          <path d="M50 22 C38 20 27 18 18 12 M50 32 C37 30 25 29 15 23 M50 42 C36 40 23 39 13 33 M50 52 C35 51 21 51 11 45 M50 62 C35 63 22 64 13 57 M50 72 C37 74 25 76 17 68" strokeWidth="5" />
          <path d="M50 22 C62 20 73 18 82 12 M50 32 C63 30 75 29 85 23 M50 42 C64 40 77 39 87 33 M50 52 C65 51 79 51 89 45 M50 62 C65 63 78 64 87 57 M50 72 C63 74 75 76 83 68" strokeWidth="5" />
          <path d="M50 88 C38 73 24 79 23 93 C36 94 44 95 50 88 Z" strokeWidth="5" />
          <path d="M50 88 C62 73 76 79 77 93 C64 94 56 95 50 88 Z" strokeWidth="5" />
        </>
      );
    case "nkyinkyim":
      return (
        <>
          <path d="M22 20 C45 20 45 38 68 38 C80 38 82 27 73 24" />
          <path d="M78 80 C55 80 55 62 32 62 C20 62 18 73 27 76" />
          <path d="M32 38 L68 62" />
          <path d="M68 38 L32 62" />
          <circle cx="50" cy="50" r="7" />
        </>
      );
    case "binkabi":
      return (
        <>
          <path d="M50 50 C29 27 18 18 9 29 C-1 41 16 58 50 50 Z" strokeWidth="8" />
          <path d="M50 50 C71 27 82 18 91 29 C101 41 84 58 50 50 Z" strokeWidth="8" />
          <path d="M50 50 C27 71 18 82 29 91 C41 101 58 84 50 50 Z" strokeWidth="8" />
          <path d="M50 50 C73 71 82 82 71 91 C59 101 42 84 50 50 Z" strokeWidth="8" />
          <circle cx="50" cy="50" r="9" strokeWidth="8" />
          <path d="M33 33 L23 23 M67 33 L77 23 M33 67 L23 77 M67 67 L77 77" strokeWidth="7" />
        </>
      );
    case "gyenyame":
      return (
        <>
          <path d="M50 15 C34 23 26 37 31 49 C35 60 50 58 55 48" strokeWidth="12" />
          <path d="M50 85 C66 77 74 63 69 51 C65 40 50 42 45 52" strokeWidth="12" />
          <path d="M31 73 C24 55 30 34 50 25" strokeWidth="8" />
          <path d="M69 27 C76 45 70 66 50 75" strokeWidth="8" />
          <path d="M38 50 L62 50" strokeWidth="10" />
        </>
      );
    case "akoben":
      return (
        <>
          <path d="M21 68 C37 50 54 39 78 32" />
          <path d="M78 32 C84 47 79 62 67 71 C54 80 35 78 25 66" />
          <path d="M34 62 C43 67 56 63 63 54 C69 46 69 38 64 34" />
          <path d="M20 67 L32 80" />
          <path d="M28 58 L40 71" />
        </>
      );
    case "akofena":
      return (
        <>
          <path d="M24 78 L76 26" />
          <path d="M76 78 L24 26" />
          <path d="M68 18 L82 32 L75 39 L61 25 Z" />
          <path d="M32 18 L18 32 L25 39 L39 25 Z" />
          <path d="M38 64 L24 78" />
          <path d="M62 64 L76 78" />
          <circle cx="50" cy="52" r="5" />
        </>
      );
    case "mpatapo":
    default:
      return (
        <>
          <path d="M50 22 C30 22 22 35 31 47 C38 56 50 50 50 50 C50 50 62 44 69 53 C78 65 70 78 50 78" />
          <path d="M50 22 C70 22 78 35 69 47 C62 56 50 50 50 50 C50 50 38 44 31 53 C22 65 30 78 50 78" />
          <circle cx="50" cy="50" r="7" />
        </>
      );
  }
}

export function AdinkraSymbol({
  name = "adinkrahene",
  className,
  color = "currentColor",
  strokeWidth = 4,
  title,
}: AdinkraSymbolProps) {
  return (
    <svg
      viewBox="0 0 100 100"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      style={color === "currentColor" ? undefined : { color }}
    >
      {title && <title>{title}</title>}
      {symbolPaths(name)}
    </svg>
  );
}

interface AdinkraPatternProps {
  className?: string;
  color?: string;
  /** Opacity of the whole pattern layer. */
  opacity?: number;
}

/**
 * A faint, full-bleed tiling watermark of Adinkra-inspired motifs. Drop into a
 * `relative` container as an absolutely-positioned, pointer-events-none layer.
 */
export function AdinkraPattern({
  className = "",
  color = "currentColor",
  opacity = 0.06,
}: AdinkraPatternProps) {
  const patternId = `adinkra-tile-${useId().replace(/:/g, "")}`;

  return (
    <svg
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
      aria-hidden="true"
      style={color === "currentColor" ? { opacity } : { opacity, color }}
    >
      <defs>
        <pattern
          id={patternId}
          width="180"
          height="180"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(0)"
        >
          <g fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <g transform="translate(12 12) scale(0.36)">{symbolPaths("sankofa")}</g>
            <g transform="translate(72 12) scale(0.36)">{symbolPaths("eban")}</g>
            <g transform="translate(132 12) scale(0.36)">{symbolPaths("nsoromma")}</g>
            <g transform="translate(12 72) scale(0.36)">{symbolPaths("dwennimmen")}</g>
            <g transform="translate(72 72) scale(0.36)">{symbolPaths("gyenyame")}</g>
            <g transform="translate(132 72) scale(0.36)">{symbolPaths("binkabi")}</g>
            <g transform="translate(12 132) scale(0.36)">{symbolPaths("fawohodie")}</g>
            <g transform="translate(72 132) scale(0.36)">{symbolPaths("adinkrahene")}</g>
            <g transform="translate(132 132) scale(0.36)">{symbolPaths("neaonnim")}</g>
          </g>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${patternId})`} />
    </svg>
  );
}

interface KenteStripeProps {
  className?: string;
  /** Height utility classes, e.g. "h-2". */
  heightClass?: string;
}

/** Kente-cloth inspired woven colour band. */
export function KenteStripe({ className = "", heightClass = "h-2" }: KenteStripeProps) {
  return (
    <div
      className={`flex w-full overflow-hidden ${heightClass} ${className}`}
      aria-hidden="true"
    >
      <span className="h-full flex-[3] bg-[#006B3F]" />
      <span className="h-full flex-[1] bg-[#0b1220]" />
      <span className="h-full flex-[2] bg-[#FCD116]" />
      <span className="h-full flex-[1] bg-[#CE1126]" />
      <span className="h-full flex-[2] bg-[#FCD116]" />
      <span className="h-full flex-[1] bg-[#0b1220]" />
      <span className="h-full flex-[3] bg-[#006B3F]" />
      <span className="h-full flex-[1] bg-[#CE1126]" />
      <span className="h-full flex-[2] bg-[#FCD116]" />
      <span className="h-full flex-[1] bg-[#0b1220]" />
      <span className="h-full flex-[3] bg-[#CE1126]" />
    </div>
  );
}
